import React, { Fragment, useEffect, useState } from 'react'
import {
  Assignment,
  CheckCircle,
  Delete,
  PlaylistAddCheck,
  SupervisedUserCircle,
  Visibility
} from '@mui/icons-material'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { FilterComponent } from '../../components/_WMS/FilterComponent'
import { GenericTableV2 } from '../../components/_EOS/GenericTable/GenericTableV2'
import { SnackBar } from '../../components/SnackBar/Snackbar'

import { useApi } from '../../hooks/useApi'
import { usePagination } from '../../hooks/usePagination'
import { useModal } from '../../hooks/useModal'
import { useSnackbar } from '../../hooks/useSnackbar'
import { useUser } from '../../hooks/useUser'
import { useConfiguration } from '../../hooks/useConfiguration'
import { useProductOrigins } from '../../hooks/useProductOrigins'

import { filterOptions, defaultFilters } from './config/filterOptions'
import { getColumnsConfig } from './config/tableColumns'
import { getAsns, deleteAsn, receiveAsn } from './config/api'
import { ASNDeleteModal } from './components/ASNDeleteModal'
import { ASNViewModal } from './components/ASNViewModal'
import { ASNReceiveModal } from './components/ASNReceiveModal'
import { ASNReceiveExternalModal } from './components/ASNReceiveExternalModal'
import { ASNBlogModal } from './components/ASNBlogModal'
import { ASNReassignLpnModal } from './components/ASNReassignLpnModal'
import { ASNLpnStateModal } from './components/ASNLpnStateModal'

import { Asn } from '../../interfaces/Asn/Asn'
import { Filter } from '../../components/_WMS/FilterComponent/types'
import { Status } from '../../hooks/useFetchState'
import { ApiResponse } from '../../typings/ApiResponse'
import { asnDeleteStates, eAsnState } from '../../enums/eAsnState'
import { eTypeStorage } from '../../enums/eTypeStorage'
import { GenericOrderBy } from '../../components/_EOS/Table/types'

export const ASNPage: React.FC = () => {
  const { callApi, state } = useApi<ApiResponse<Asn>>()
  const pagination = usePagination(5)
  const { isActive, message, openSnackBar, isSuccess } = useSnackbar()
  const {
    userDataState: { userData }
  } = useUser()
  const { configurationState, getConfiguration } = useConfiguration()
  const {
    productOriginsState: { productOrigins },
    getProductOrigins
  } = useProductOrigins()

  const [selectedFilters, setSelectedFilters] = useState<Filter[]>(defaultFilters)
  const [isAllowedTwoUsersOnSameLPN, setIsAllowedTwoUsersOnSameLPN] =
    useState(false)

  // Estados para reasignación de LPN
  const [selectedUser, setSelectedUser] = useState<string>('')
  const [, setLpnsSelected] = useState<string[]>([])
  const [, setIsErrorSelectedUser] = useState<boolean>(false)

  // Estado de ordenamiento
  const [orderBy, setOrderBy] = useState<GenericOrderBy<Asn>>({
    field: 'asnId' as keyof Asn,
    direction: 'desc'
  })

  // Estado para porceso de recepción
  const [isReceiving, setIsReceiving] = useState(false)

  const deleteModal = useModal<Asn>('Confirmar Eliminación')
  const receivedModal = useModal<Asn>('Recibir ASN')
  const viewModal = useModal<Asn>('Detalles de ASN')
  const blogModal = useModal<Asn>('Bitácora ASN')
  const receivedExternalModal = useModal<Asn>(
    'Recibir ASN Almacenamiento Externo'
  )
  const userReassignLpnModal = useModal<Asn>('Reasignación de usuario a Lpn')
  const lpnStateModal = useModal<Asn>('Abrir Lpn')

  const columnsConfig = getColumnsConfig(productOrigins)

  const requestData = (
    filters: Filter[],
    page: number,
    pageSize: number,
    supplierId?: number
  ) => {
    callApi(getAsns(filters, page, pageSize, supplierId, orderBy)).catch(
      (error) => {
        openSnackBar(error, false)
      }
    )
  }

  const handleSorting = (field: keyof Asn, direction: 'asc' | 'desc') => {
    setOrderBy({ field, direction })
  }

  const handlePagination = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestData(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const handleDelete = (asn: Asn) => {
    callApi(deleteAsn(asn.asnId))
      .then(() => {
        openSnackBar('ASN eliminado correctamente', true)
        requestData(selectedFilters, pagination.page, pagination.pageSize)
        deleteModal.closeModal()
      })
      .catch((error) => {
        openSnackBar(error, false)
      })
  }

  const handleReceiveAsn = (
    asn: Asn,
    comment?: string,
    isReserve?: boolean
  ) => {
    if (!userData?.userId) {
      openSnackBar('Usuario no encontrado', false)
      return
    }
    setIsReceiving(true)
    callApi(receiveAsn(asn.asnId, userData.userId, isReserve, comment))
      .then((response) => {
        const snackbarMessage =
          response.asnDetails.length <= 100
            ? 'Se ha recepcionado correctamente el ASN N°'
            : 'El proceso se ha creado correctamente, consulte el menú Procesos para verificar el estado de su solicitud. ASN N°'

        openSnackBar(`${snackbarMessage} ${asn.asnId}`, true)
        requestData(selectedFilters, pagination.page, pagination.pageSize)
        receivedModal.closeModal()
        receivedExternalModal.closeModal()
      })
      .catch((error) => {
        openSnackBar(error, false)
      })
      .finally(() => {
        setIsReceiving(false)
      })
  }

  const getStateForReceived = (asnStateId: number): boolean => {
    return asnStateId !== eAsnState.Pending
  }

  const getStateForDelete = (asn: Asn): boolean => {
    return !(
      asnDeleteStates.includes(asn.asnStateId) &&
      (asn.locatedQty === 0 || asn.locatedQty === null)
    )
  }

  useEffect(() => {
    // al cambiar de filtros debe volver a pagina 0
    pagination.setPagination(0, pagination.pageSize)
    // se obtiene al tener filtros vacios (al limpiar filtros despues busca)
    if (selectedFilters.length === 0) {
      requestData(selectedFilters, 0, pagination.pageSize)
    }
  }, [selectedFilters, pagination.pageSize])

  useEffect(() => {
    if (configurationState === undefined) {
      getConfiguration()
    } else {
      const externalCourierLabelConfiguration =
        configurationState.configuration.filter(
          (el) => el.name === 'AllowTwoUserOnTheSameLPN'
        )[0]

      if (externalCourierLabelConfiguration !== undefined) {
        setIsAllowedTwoUsersOnSameLPN(
          externalCourierLabelConfiguration.value === 'true'
        )
      }
    }
  }, [configurationState])

  useEffect(() => {
    getProductOrigins()
  }, [])

  useEffect(() => {
    requestData(selectedFilters, pagination.page, pagination.pageSize)
  }, [orderBy])

  return (
    <Fragment>
      <StandardPage pageTitle='Gestionar ASN'>
        <FilterComponent
          includeAddButton={true}
          options={filterOptions}
          onSearchClick={() =>
            requestData(selectedFilters, pagination.page, pagination.pageSize)
          }
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
        />

        <GenericTableV2<Asn>
          data={state.data?.items || []}
          columns={columnsConfig}
          keyField='asnId'
          loading={state.status === Status.Loading}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePagination
          }}
          sorting={{
            field: orderBy.field as keyof Asn,
            direction: orderBy.direction,
            onSortChange: handleSorting
          }}
          styling={{
            dense: false,
            hoverEffect: true
          }}
          actions={[
            {
              key: 'blog',
              label: 'Ver Bitácora',
              onClick: (item) => blogModal.openModal('md', item),
              icon: <Assignment />
            },
            {
              key: 'view',
              label: 'Ver ASN',
              onClick: (item) => viewModal.openModal('md', item),
              icon: <Visibility />
            },
            {
              key: 'receive',
              label: 'Recepcionar',
              onClick: (item) => {
                if (item.typeStorageId === eTypeStorage.Externo) {
                  receivedExternalModal.openModal('sm', item)
                } else {
                  receivedModal.openModal('sm', item)
                }
              },
              icon: <CheckCircle />,
              disabled: (item) => getStateForReceived(item.asnStateId)
            },
            {
              key: 'delete',
              label: 'Anular',
              onClick: (item) => deleteModal.openModal('sm', item),
              icon: <Delete />,
              disabled: (item) => getStateForDelete(item)
            },
            {
              key: 'reassign',
              label: 'Reasignación usuario Lpn',
              onClick: (item) => userReassignLpnModal.openModal('sm', item),
              icon: <SupervisedUserCircle />,
              disabled: () => isAllowedTwoUsersOnSameLPN
            },
            {
              key: 'lpnState',
              label: 'Liberar estado LPN',
              onClick: (item) => lpnStateModal.openModal('sm', item),
              icon: <PlaylistAddCheck />,
              disabled: (item) => !(item.asnStateId === eAsnState.Locating)
            }
          ]}
        />
      </StandardPage>

      <ASNDeleteModal deleteModalState={deleteModal} onConfirm={handleDelete} />

      <ASNViewModal viewModalState={viewModal} />

      <ASNReceiveModal
        receivedModalState={receivedModal}
        onReceive={handleReceiveAsn}
        isLoading={isReceiving}
      />

      <ASNReceiveExternalModal
        receivedExternalModalState={receivedExternalModal}
        onReceive={handleReceiveAsn}
      />

      <ASNBlogModal blogModalState={blogModal} />

      <ASNReassignLpnModal
        userReassignLpnModal={userReassignLpnModal}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        setLpnsSelected={setLpnsSelected}
        isErrorSelectedUser={setIsErrorSelectedUser}
      />

      <ASNLpnStateModal lpnStateModal={lpnStateModal} />

      <SnackBar
        message={message}
        status={isSuccess ? 'success' : 'error'}
        open={isActive}
      />
    </Fragment>
  )
}
